import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ArrowRight, Lock, ShieldCheck, Wifi, Zap } from 'lucide-react';
import { useAuth } from '@/lib/auth/useAuth';
import logoMark from '@/assets/logo-mark.png';

const TRUST_BADGES = [
  { icon: Zap,        label: 'No OTP friction' },
  { icon: Wifi,       label: 'Network-backed' },
  { icon: Lock,       label: 'Role-based access' },
  { icon: ShieldCheck, label: 'Secure by design' },
];

const STATS = [
  { num: '99.9%', label: 'Uptime SLA' },
  { num: '<500ms', label: 'Avg latency' },
  { num: '50M+',  label: 'Verifications' },
];

const FEATURES = [
  'Silent mobile identity — no OTP friction',
  'Network-level trust signals, telecom-grade uptime',
  'End-to-end encrypted, full audit trail',
  'Privacy-conscious, GDPR & ISO 27001 ready',
];

const CERTS = ['ISO 27001', 'GDPR', 'SOC 2'];

/* Force dark navy — auth page is always dark regardless of user theme */
const D = {
  page:       '#050e1d',
  leftBg:     '#060D1A',
  leftBorder: 'rgba(96,165,250,0.08)',
  iconBox:    'rgba(59,130,246,0.16)',
  iconBorder: 'rgba(96,165,250,0.22)',
  statBorder: 'rgba(96,165,250,0.12)',
  statSep:    'rgba(96,165,250,0.10)',
  statBg:     'rgba(255,255,255,0.02)',
  checkBox:   'rgba(52,211,153,0.12)',
  checkBorder:'rgba(52,211,153,0.20)',
  certBg:     'rgba(255,255,255,0.05)',
  certBorder: 'rgba(255,255,255,0.10)',
  rightBg:    'radial-gradient(ellipse at 65% 35%, rgba(59,130,246,0.09) 0%, transparent 55%), radial-gradient(ellipse at 20% 75%, rgba(99,102,241,0.06) 0%, transparent 50%), linear-gradient(160deg, #060f22 0%, #050e1d 100%)',
  cardBg:     'linear-gradient(135deg, rgba(5,14,29,0.92) 0%, rgba(6,15,34,0.88) 100%)',
  cardBorder: 'rgba(96,165,250,0.13)',
  cardShadow: '0 0 0 1px rgba(255,255,255,0.04) inset, 0 1px 0 rgba(255,255,255,0.07) inset, 0 4px 24px rgba(0,0,0,0.45), 0 20px 60px rgba(0,0,0,0.35), 0 0 80px rgba(59,130,246,0.05)',
  btnBg:      'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  btnShadow:  '0 1px 0 rgba(255,255,255,0.12) inset, 0 2px 8px rgba(37,99,235,0.40), 0 6px 24px rgba(37,99,235,0.28)',
  btnHover:   'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  badgeBg:    'rgba(255,255,255,0.03)',
  badgeBorder:'rgba(96,165,250,0.10)',
  footerBorder:'rgba(96,165,250,0.08)',
  errBg:      'rgba(239,68,68,0.10)',
  errBorder:  'rgba(239,68,68,0.28)',
  logoGlow:   '0 0 32px rgba(59,130,246,0.15)',
} as const;

export function LoginPage() {
  const { login, isAuthenticated, isInitializing } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [loginError, setLoginError]   = useState(false);
  const [btnHovered, setBtnHovered]   = useState(false);

  if (isInitializing) {
    return (
      <div style={{ background: D.page }} className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
        <p style={{ color: 'rgba(255,255,255,0.40)', fontSize: '13.5px' }}>Preparing secure sign-in…</p>
      </div>
    );
  }

  if (isAuthenticated) return <Navigate to="/overview" replace />;

  function handleLogin() {
    setLoginError(false);
    setIsRedirecting(true);
    try {
      login();
    } catch {
      setIsRedirecting(false);
      setLoginError(true);
    }
  }

  return (
    <div className="flex min-h-screen" style={{ background: D.page, fontFamily: "'Inter', -apple-system, sans-serif" }}>

      {/* ── LEFT BRAND PANEL ──────────────────────────────────────── */}
      <div
        className="relative hidden w-[46%] min-w-[380px] flex-col justify-between overflow-hidden lg:flex"
        style={{ background: D.leftBg, borderRight: `1px solid ${D.leftBorder}` }}
        aria-hidden="true"
      >
        {/* Dot grid overlay */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.055) 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        {/* Glow blobs */}
        <div className="pointer-events-none absolute -right-36 -top-36 h-[560px] w-[560px] rounded-full"
             style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.16) 0%, rgba(37,99,235,0.06) 50%, transparent 70%)' }} />
        <div className="pointer-events-none absolute -bottom-24 -left-20 h-[400px] w-[400px] rounded-full"
             style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 65%)' }} />

        <div className="relative z-10 flex flex-1 flex-col justify-between p-14">

          {/* Logo row */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]"
                 style={{ background: D.iconBox, border: `1px solid ${D.iconBorder}` }}>
              <img src={logoMark} alt="" className="h-[22px] w-auto object-contain"
                   style={{ filter: 'brightness(0) invert(1)' }} />
            </div>
            <div>
              <span className="block text-[18px] font-bold tracking-tight text-white">Authmatech</span>
              <span className="block text-[10px] font-medium tracking-[0.12em] uppercase"
                    style={{ color: 'rgba(255,255,255,0.35)' }}>Identity Platform</span>
            </div>
          </div>

          {/* Hero */}
          <div>
            <h2
              className="mb-4 text-[2.05rem] font-bold leading-[1.22] text-white"
              style={{ letterSpacing: '-0.6px' }}
            >
              Silent mobile<br />
              <span style={{
                background: 'linear-gradient(135deg, #93c5fd 0%, #60a5fa 50%, #818cf8 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>verification</span><br />
              at enterprise scale.
            </h2>
            <p className="mb-8 max-w-[340px] text-[15px] leading-[1.65]"
               style={{ color: 'rgba(255,255,255,0.45)' }}>
              Confirm ownership of any mobile number — no OTP, no friction —
              directly through the operator network.
            </p>

            {/* Stats row */}
            <div
              className="mb-8 grid grid-cols-3 overflow-hidden rounded-xl"
              style={{ border: `1px solid ${D.statBorder}`, background: D.statBg }}
            >
              {STATS.map(({ num, label }, i) => (
                <div
                  key={label}
                  className="flex flex-col items-center px-2 py-3.5"
                  style={{ borderRight: i < STATS.length - 1 ? `1px solid ${D.statSep}` : 'none' }}
                >
                  <span className="text-[17px] font-bold text-white" style={{ letterSpacing: '-0.3px' }}>{num}</span>
                  <span className="mt-0.5 text-center text-[10.5px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{label}</span>
                </div>
              ))}
            </div>

            {/* Feature checklist */}
            <div className="flex flex-col gap-3.5">
              {FEATURES.map((text) => (
                <div key={text} className="flex items-center gap-3">
                  <div
                    className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[7px]"
                    style={{ background: D.checkBox, border: `1px solid ${D.checkBorder}` }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                         stroke="#34d399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                         aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <span className="text-[13.5px]" style={{ color: 'rgba(255,255,255,0.50)' }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom — certification chips */}
          <div className="flex flex-wrap items-center gap-2">
            {CERTS.map((cert) => (
              <div
                key={cert}
                className="rounded-md px-2.5 py-1 text-[10.5px] font-medium"
                style={{ background: D.certBg, border: `1px solid ${D.certBorder}`, color: 'rgba(255,255,255,0.35)' }}
              >
                {cert}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ──────────────────────────────────────── */}
      <div
        className="relative flex flex-1 flex-col items-center justify-center overflow-hidden px-6 py-12"
        style={{ background: D.rightBg }}
      >
        {/* Ambient glow */}
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 68%)' }}
          aria-hidden
        />

        {/* Mobile logo */}
        <div className="relative z-10 mb-8 flex items-center gap-3 lg:hidden">
          <img src={logoMark} alt="Authmatech" className="h-8 w-auto"
               style={{ filter: 'brightness(0) invert(1)' }} />
          <span className="text-[18px] font-bold text-white">Authmatech</span>
        </div>

        {/* Glass card */}
        <div
          className="relative z-10 w-full max-w-[420px]"
          style={{
            background: D.cardBg,
            border: `1px solid ${D.cardBorder}`,
            borderRadius: '16px',
            padding: '44px 40px',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            boxShadow: D.cardShadow,
          }}
        >
          {/* Top edge highlight */}
          <div
            className="pointer-events-none absolute left-[12%] right-[12%] top-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(96,165,250,0.35), transparent)', borderRadius: '50%' }}
            aria-hidden
          />

          {/* Logo icon */}
          <div className="mb-6 flex justify-center">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-2xl"
              style={{ background: D.iconBox, border: `1.5px solid ${D.iconBorder}`, boxShadow: D.logoGlow }}
            >
              <img src={logoMark} alt="" className="h-8 w-8 object-contain"
                   style={{ filter: 'brightness(0) invert(1)' }} aria-hidden />
            </div>
          </div>

          <h2
            className="mb-2 text-center text-[22px] font-bold text-white"
            style={{ letterSpacing: '-0.5px' }}
          >
            Welcome back
          </h2>
          <p
            className="mb-8 text-center text-[13.5px] leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.42)' }}
          >
            Sign in to your Authmatech workspace<br />
            via Authmatech Identity.
          </p>

          {/* Error state */}
          {loginError && (
            <div
              className="mb-5 flex items-center gap-2.5 rounded-[10px] p-3.5 text-[13.5px]"
              style={{ background: D.errBg, border: `1px solid ${D.errBorder}`, color: '#fca5a5' }}
            >
              <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
              We couldn't start sign-in. Please try again.
            </div>
          )}

          {/* CTA button */}
          <button
            onClick={handleLogin}
            disabled={isRedirecting}
            onMouseEnter={() => setBtnHovered(true)}
            onMouseLeave={() => setBtnHovered(false)}
            className="relative mt-1 flex w-full items-center justify-center gap-2 overflow-hidden font-semibold text-white transition-all duration-150"
            style={{
              height: '46px',
              fontSize: '15px',
              letterSpacing: '0.1px',
              background: btnHovered && !isRedirecting ? D.btnHover : D.btnBg,
              border: 'none',
              borderRadius: '10px',
              cursor: isRedirecting ? 'not-allowed' : 'pointer',
              opacity: isRedirecting ? 0.65 : 1,
              boxShadow: btnHovered && !isRedirecting
                ? '0 1px 0 rgba(255,255,255,0.12) inset, 0 4px 12px rgba(37,99,235,0.50), 0 8px 28px rgba(37,99,235,0.35)'
                : D.btnShadow,
            }}
          >
            {isRedirecting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                <span>Preparing secure sign-in…</span>
              </>
            ) : (
              <>
                <span>Continue to Authmatech Identity</span>
                <ArrowRight className="h-4 w-4" aria-hidden />
              </>
            )}
          </button>

          {/* Trust badges */}
          <div className="mt-6 grid grid-cols-2 gap-2">
            {TRUST_BADGES.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-medium"
                style={{ background: D.badgeBg, border: `1px solid ${D.badgeBorder}`, color: 'rgba(255,255,255,0.40)' }}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: '#60a5fa' }} aria-hidden />
                {label}
              </div>
            ))}
          </div>

          {/* Secure footer */}
          <div
            className="mt-6 flex items-center justify-center gap-1.5 border-t pt-4 text-[11.5px]"
            style={{ borderColor: D.footerBorder, color: 'rgba(255,255,255,0.22)' }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                 stroke="rgba(96,165,250,0.60)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                 aria-hidden>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Protected by Authmatech Identity
          </div>
        </div>

        <p className="relative z-10 mt-5 text-center text-[12px]"
           style={{ color: 'rgba(255,255,255,0.20)' }}>
          Access issues? Contact your Authmatech administrator.
        </p>
      </div>
    </div>
  );
}
